import PropTypes from "prop-types"
import React from "react"

import Button from "src/components/Button"
import Table from "src/components/Table"

import { SelectionPropType } from "./selection"

const DataTableHeadRow = ({
  children,
  isSelectionEnabled,
  allPageRowsSelected,
  selectedCount,
  totalSelectableCount,
  pageSelectableCount,
  onSelectAll,
  onDeselectAll,
  onSelectAllAcrossPages,
  showSelectAllBanner,
  selectionActions = [],
  selection,
  hideSettings,
  hideActions,
  renderColumnCheckBoxes,
  visibleColumnsCount,
}) => {
  const hasSelection = selectedCount > 0
  const noSelectableRowsOnPage = pageSelectableCount === 0
  const noSelectableRowsAtAll = totalSelectableCount === 0
  const renderSelectAllBanner = () => {
    if (!showSelectAllBanner) return null

    return (
      <tr className="border-b bg-blue-50">
        <td
          colSpan={
            visibleColumnsCount +
            (isSelectionEnabled ? 1 : 0) +
            (!hideSettings || !hideActions ? 1 : 0)
          }
        >
          <div className="flex items-center justify-center gap-1 py-2 text-sm">
            <span className="text-gray-600">
              All {pageSelectableCount} on this page selected.
            </span>
            <Button variant="link" onClick={onSelectAllAcrossPages}>
              Select all {totalSelectableCount.toLocaleString()} items
            </Button>
          </div>
        </td>
      </tr>
    )
  }
  const totalColSpan =
    visibleColumnsCount +
    (isSelectionEnabled ? 1 : 0) +
    (!hideSettings || !hideActions ? 1 : 0)

  const renderSelectionToolbar = () => {
    if (!isSelectionEnabled) return null

    return (
      <tr className="h-10 border-b bg-white">
        <td colSpan={totalColSpan} className="px-4 py-3">
          <div className="flex h-6 items-center gap-4">
            <Button
              variant="link"
              onClick={onSelectAll}
              disabled={
                allPageRowsSelected ||
                noSelectableRowsOnPage ||
                noSelectableRowsAtAll
              }
            >
              Select all
            </Button>
            <Button
              variant="link"
              onClick={onDeselectAll}
              disabled={!hasSelection}
            >
              Deselect all
            </Button>
            {hasSelection && (
              <>
                <span className="text-sm text-gray-600">
                  <span className="font-semibold">
                    {selectedCount.toLocaleString()}
                  </span>{" "}
                  selected
                </span>
                {selectionActions.length > 0 && (
                  <span className="text-gray-300">|</span>
                )}
                {selectionActions.map((action, index) => (
                  <Button
                    key={index}
                    icon={action.icon}
                    title={action.label}
                    variant="secondary"
                    small={true}
                    onClick={() => action.onClick(selection)}
                  >
                    {action.label}
                  </Button>
                ))}
              </>
            )}
          </div>
        </td>
      </tr>
    )
  }

  return (
    <>
      {renderSelectionToolbar()}
      {renderSelectAllBanner()}
      <tr className="relative h-10 border-b bg-gray-200 group-[.is-infinite]:rounded-t group-[.is-infinite]:border-0">
        {isSelectionEnabled && <th style={{ width: "50px" }} />}
        {children}
        {!hideSettings ? (
          <Table.Head.Cell columnWidth="60px">
            <div className="flex justify-end">{renderColumnCheckBoxes()}</div>
          </Table.Head.Cell>
        ) : (
          !hideActions && <Table.Head.Cell columnWidth="60px" />
        )}
      </tr>
    </>
  )
}

DataTableHeadRow.propTypes = {
  allPageRowsSelected: PropTypes.bool,
  children: PropTypes.node.isRequired,
  hideActions: PropTypes.bool,
  hideSettings: PropTypes.bool,
  isSelectionEnabled: PropTypes.bool,
  onDeselectAll: PropTypes.func,
  onSelectAll: PropTypes.func,
  onSelectAllAcrossPages: PropTypes.func,
  pageSelectableCount: PropTypes.number,
  renderColumnCheckBoxes: PropTypes.func,
  selectedCount: PropTypes.number,
  selection: SelectionPropType,
  selectionActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      variant: PropTypes.string,
    })
  ),
  showSelectAllBanner: PropTypes.bool,
  totalSelectableCount: PropTypes.number,
  visibleColumnsCount: PropTypes.number,
}

export default DataTableHeadRow
